import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/helpers';

const variants = {
  primary: 'bg-accent text-primary shadow-neutral hover:shadow-neutral-lg hover:-translate-y-0.5',
  secondary: 'border-2 border-primary text-primary hover:bg-primary hover:text-white',
  outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-primary shadow-xs',
  light: 'bg-white/10 text-white border-2 border-white/30 hover:bg-white hover:text-primary',
  ghost: 'text-primary hover:bg-primary/5',
  link: 'text-primary underline-offset-4 hover:underline',
};

const sizes = {
  xs: 'px-3 py-1.5 text-xs',
  sm: 'px-5 py-2 text-sm',
  md: 'px-8 py-3 text-base',
  lg: 'px-10 py-4 text-lg',
};

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  href,
  to,
  className,
  disabled,
  loading,
  leftIcon,
  rightIcon,
  ...props
}, ref) => {
  const baseStyles = cn(
    'inline-flex items-center justify-center gap-2 font-medium btn transition-all duration-300',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
    variants[variant],
    sizes[size],
    className
  );
  
  const content = (
    <>
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4\" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {leftIcon && !loading && <span className="flex-shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </>
  );
  
  // External link
  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        className={baseStyles}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {content}
      </a>
    );
  }
  
  // Internal link (React Router)
  if (to) {
    return (
      <Link
        ref={ref}
        to={to}
        className={baseStyles}
        {...props}
      >
        {content}
      </Link>
    );
  }
  
  // Regular button
  return (
    <button
      ref={ref}
      className={baseStyles}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
